import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { styled } from '@mui/material/styles';

import { Tooltip } from '@zendesk/forethought-elements';
import { UnstyledUnorderedList } from '../../navbar/common/resetUI';
import { SIDEBAR_HIGHLIGHT_COLOR } from '../constants';
import {
  insertDynamicAnalyticsTab,
  isRouteExperimentEnabled,
  matchesRoute,
  processBadgedNavItems,
  processBadges,
} from '../helpers';
import { NavDataItem } from '../types';
import navData from './navData';
import NavGroup from './navGroup';
import NavMenuItem from './navMenuItem';
import SidebarAITrigger from './SidebarAITrigger';
import cloneDeep from 'lodash/fp/cloneDeep';
import logoMini from 'src/assets/images/ft-logo-white.png';
import ToggleCollapseIcon from 'src/assets/images/sidebar-toggle-collapse-new.svg';
import ToggleExpandIcon from 'src/assets/images/sidebar-toggle-expand-new.svg';
import { useAIStudio } from 'src/components/global-ai-studio/AIStudioContext';
import useDiscoverSectionViewable from 'src/hooks/discover/useDiscoverSectionViewable';
import { useBadgedRoutes } from 'src/hooks/useBadgedRoutes';
import useEmbeddedDashboardSettings from 'src/hooks/useEmbeddedDashboardSettings';
import { useIsZendeskBuild } from 'src/hooks/useIsZendeskBuild';
import {
  selectUserEmail,
  selectUserRole,
} from 'src/reducers/userReducer/userReducer';
import {
  useGetFeatureFlagsQuery,
  useGetPaidPlanDetailsQuery,
  useGetPermissionsByRoleQuery,
  useGetPermissionsQuery,
} from 'src/services/dashboard-api';
import { useGetEmbeddedAnalyticsSettingsQuery } from 'src/services/embedded-analytics/embeddedAnalyticsApi';
import { useGetErrorEventCountQuery } from 'src/services/error-events/errorEventsApi';
import { useGetOrgConfigQuery } from 'src/services/settings/settingsApi';
import { selectExperimentTreatments } from 'src/slices/experiments/experimentsSlice';
import { Permission, Tabs, UserRoles } from 'src/utils/enums';

type SidebarProps = {
  isFullWidth?: boolean;
  isSidebarExpandedToggle: boolean;
  permissions: Set<string>;
  toggleSidebarFlag: () => void;
};

const NewSidebar: FunctionComponent<React.PropsWithChildren<SidebarProps>> = ({
  isFullWidth = true,
  permissions,
  toggleSidebarFlag,
}) => {
  const { t } = useTranslation('app');
  const [activeNavGroup, setActiveNavGroup] = useState<string | null>(null);
  const {
    isLoading: isAIStudioLoading,
    // When the side-panel UX flag is on, the sidebar trigger moves to the
    // navbar (next to account settings) — see navbarV2.tsx.
    isSidePanelAvailable: isAIStudioNavbarUxEnabled,
    open: openAIStudio,
  } = useAIStudio();
  const isZendeskBuild = useIsZendeskBuild();
  const userRole = useSelector(selectUserRole);
  const userEmail = useSelector(selectUserEmail);
  const { pathname } = useLocation();
  const treatments = useSelector(selectExperimentTreatments);
  const { activeBadgedRoutes } = useBadgedRoutes(pathname);
  const embeddedDashboardSettings = useEmbeddedDashboardSettings();
  const { data: featureFlagsData } = useGetFeatureFlagsQuery();
  const { feature_flags: featureFlags = [] } = featureFlagsData ?? {};
  const { data: orgConfigData } = useGetOrgConfigQuery();
  const { data: paidPlanDetails } = useGetPaidPlanDetailsQuery();
  const { data: permissionsByRole = {} } = useGetPermissionsByRoleQuery();
  // The settings endpoint is admin-only; other roles would just get a 403.
  const { data: embeddedAnalyticsUserSettings } =
    useGetEmbeddedAnalyticsSettingsQuery(undefined, {
      skip:
        userRole !== UserRoles.ROLE_SUPER_ADMIN &&
        userRole !== UserRoles.ROLE_ADMIN,
    });
  const isSolveWidgetEnabled = featureFlags.includes('solve_widget_enabled');
  const { data: appPermissions = {} } = useGetPermissionsQuery();
  const isAIStudioEnabled = Boolean(appPermissions[Permission.AI_STUDIO]);
  const {
    discoverActionsEnabled,
    discoverAnalyticsEnabled,
    showInternalBadgeForActions,
    showInternalBadgeForAnalytics,
  } = useDiscoverSectionViewable();

  const isErrorAlertManagementEnabled = featureFlags.includes(
    'error_alert_management',
  );
  const { data: errorEventsCountResponse } = useGetErrorEventCountQuery(
    undefined,
    {
      skip: !isSolveWidgetEnabled || isErrorAlertManagementEnabled,
    },
  );
  const errorEventsCount = errorEventsCountResponse?.error_count ?? 0;
  const navItemToNotificationsMap = useMemo(
    () => ({
      [Tabs.LOG]: errorEventsCount,
    }),
    [errorEventsCount],
  );

  const navItems = useMemo(() => {
    const filteredItems = cloneDeep(navData)
      .filter(navItem => {
        // On the Zendesk-mounted build, the Triage parent tab is gated by the
        // `predictions_analytics` org permission. Off Zendesk it shows whenever
        // any child route passes the role/permission filters below.
        if (
          navItem.name === Tabs.PREDICTIONS &&
          isZendeskBuild &&
          !permissions.has(Permission.PREDICTIONS)
        ) {
          return false;
        }

        if (navItem.children) {
          const filteredChildren = navItem.children
            .filter(childNavItem =>
              isRouteExperimentEnabled(childNavItem.route?.path, treatments),
            )
            .filter(childNavItem => {
              if (
                userRole === UserRoles.ROLE_SUPER_ADMIN &&
                childNavItem.route?.ignorePermissionForSuperAdmin
              ) {
                return true;
              }

              if (childNavItem.name === Tabs.PLAN) {
                return false;
              }

              if (childNavItem.route?.permissionKey) {
                return permissions.has(childNavItem.route.permissionKey);
              }

              if (childNavItem.route?.roles) {
                return childNavItem.route.roles.has(userRole);
              }
            })
            .filter(childNavItem =>
              childNavItem.route?.featureFlags
                ? childNavItem.route?.featureFlags.every(flag => {
                    // Special handling for is_assist_gpt_enabled - check org config instead of feature flags
                    if (flag === 'is_assist_gpt_enabled') {
                      return orgConfigData?.is_assist_gpt_enabled === true;
                    }
                    return featureFlags.includes(flag);
                  })
                : true,
            )
            .filter(childNavItem =>
              childNavItem.route?.requiredTiers?.length
                ? paidPlanDetails?.tier &&
                  childNavItem.route.requiredTiers.includes(
                    paidPlanDetails.tier,
                  )
                : true,
            )
            .filter(childNavItem => {
              // SSO settings configure the dashboard's own Cognito/SAML auth,
              // which is bypassed in the Zendesk build (auth rides Zendesk's
              // session cookies — see initApp.tsx and zendeskAuth.ts).
              if (childNavItem.name === Tabs.SSO_SETTINGS && isZendeskBuild) {
                return false;
              }
              // Admin Center is a Zendesk-mounted admin app served at
              // `/admin`. Only show it on Zendesk builds.
              if (childNavItem.name === Tabs.ADMIN_CENTER && !isZendeskBuild) {
                return false;
              }
              if (
                (childNavItem.name === Tabs.AGENT_QA_INSIGHTS ||
                  childNavItem.name === Tabs.AGENT_QA_SETUP) &&
                permissions.has(Permission.AGENT_INSIGHTS)
              ) {
                return true;
              }
              if (
                [
                  Tabs.DISCOVER_DASHBOARD,
                  Tabs.DISCOVER_TOPICS,
                  Tabs.DISCOVER_INSIGHTS,
                ].includes(childNavItem.name as Tabs)
              ) {
                if (showInternalBadgeForAnalytics) {
                  childNavItem.badgeLabel = 'Internal';
                }
                return discoverAnalyticsEnabled;
              }
              if (childNavItem.name === Tabs.DISCOVER_AUTOMATION) {
                if (showInternalBadgeForActions) {
                  childNavItem.badgeLabel = 'Internal';
                }
                return discoverActionsEnabled;
              }
              return true;
            });
          if (filteredChildren.length) {
            navItem.children = filteredChildren;
          }

          return filteredChildren.length;
        }

        if (!navItem.route && navItem.children) {
          // static text, no route
          return true;
        }

        if (
          userRole === UserRoles.ROLE_SUPER_ADMIN &&
          navItem.route?.ignorePermissionForSuperAdmin
        ) {
          return true;
        }

        if (navItem.route?.permissionKey) {
          return permissions.has(navItem.route.permissionKey);
        }

        if (navItem.route?.roles) {
          return navItem.route.roles.has(userRole);
        }
      })
      .map(
        processBadges(permissionsByRole, userRole, navItemToNotificationsMap),
      )
      .map(processBadgedNavItems(activeBadgedRoutes));

    const orgHasLookerPermissions = permissions.has(
      Permission.LOOKER_ANALYTICS,
    );
    const orgHasDashboardsConfigured = embeddedDashboardSettings?.length;
    const userHasLookerPermissions =
      userRole === UserRoles.ROLE_SUPER_ADMIN ||
      (userEmail &&
        embeddedAnalyticsUserSettings?.allowed_emails.includes(userEmail));
    if (
      orgHasLookerPermissions &&
      orgHasDashboardsConfigured &&
      userHasLookerPermissions
    ) {
      return insertDynamicAnalyticsTab(
        filteredItems,
        embeddedDashboardSettings,
      );
    }

    return filteredItems;
  }, [
    permissionsByRole,
    userRole,
    activeBadgedRoutes,
    permissions,
    embeddedDashboardSettings,
    treatments,
    featureFlags,
    embeddedAnalyticsUserSettings,
    userEmail,
    navItemToNotificationsMap,
    isZendeskBuild,
    orgConfigData?.is_assist_gpt_enabled,
    paidPlanDetails,
    discoverActionsEnabled,
    discoverAnalyticsEnabled,
    showInternalBadgeForActions,
    showInternalBadgeForAnalytics,
  ]);

  useEffect(() => {
    // When route changes, open the group that contains the active child route
    const matchedGroup = navItems.find(item =>
      item.children?.some(child => matchesRoute(child.route, pathname)),
    );

    if (matchedGroup) {
      setActiveNavGroup(matchedGroup.name);
    } else {
      setActiveNavGroup(null);
    }
  }, [navItems, pathname]);

  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const [recentlyToggled, setRecentlyToggled] = useState(false);
  const [minimizedFlyout, setMinimizedFlyout] = useState<{
    children?: NavDataItem[];
    left: number | null;
    openFor: string | null;
    top: number | null;
  }>({ left: null, openFor: null, top: null });
  const toggleTimeoutRef = useRef<number | null>(null);

  const clearMinimizedFlyout = useCallback(() => {
    setMinimizedFlyout({
      children: undefined,
      left: null,
      openFor: null,
      top: null,
    });
  }, []);

  const handleToggle = () => {
    // Prevent the expand icon from flashing immediately after toggling.
    setRecentlyToggled(true);
    // Use the persisted toggle so clicking the collapse/expand button
    // updates the stored expanded state (toggleSidebarFlag).
    toggleSidebarFlag();
    if (toggleTimeoutRef.current) {
      window.clearTimeout(toggleTimeoutRef.current);
    }
    toggleTimeoutRef.current = window.setTimeout(() => {
      setRecentlyToggled(false);
      toggleTimeoutRef.current = null;
    }, 220);
  };

  useEffect(() => {
    return () => {
      if (toggleTimeoutRef.current) {
        window.clearTimeout(toggleTimeoutRef.current);
      }
    };
  }, []);

  const minimizedNavMenu = useMemo(() => {
    const hasLogs = navItems.some(item => item.name === Tabs.LOG);

    return (
      <UnstyledUnorderedList style={{ padding: '0px 8px' }}>
        {navItems.map(
          ({ badgeLabel, children, icon, name, route, tooltip }) => {
            const isSelected = Boolean(
              matchesRoute(route, pathname) ||
              children?.some(child => matchesRoute(child.route, pathname)),
            );

            const isGroup = !!(children && children.length);

            const handleMinimizedClick = (e: React.MouseEvent) => {
              e.preventDefault();
              const el = e.currentTarget as HTMLElement;
              const sidebarEl = el.closest('nav') as HTMLElement | null;
              const elRect = el.getBoundingClientRect();
              const sidebarRect = sidebarEl?.getBoundingClientRect();
              const top = sidebarRect
                ? elRect.top - sidebarRect.top
                : elRect.top;
              const left = sidebarRect ? sidebarRect.right : elRect.right;

              setMinimizedFlyout(prev =>
                prev.openFor === name
                  ? {
                      children: undefined,
                      left: null,
                      openFor: null,
                      top: null,
                    }
                  : { children, left, openFor: name, top },
              );
            };

            const handleMinimizedHover = () => {
              if (!isGroup && minimizedFlyout.openFor) {
                clearMinimizedFlyout();
              }
            };

            const isMinimizedOpen = minimizedFlyout.openFor === name;

            return (
              <>
                {name === Tabs.LOG || (name === Tabs.SETTINGS && !hasLogs) ? (
                  <li key={`${name}-sep`}>
                    <Separator />
                  </li>
                ) : null}
                <li
                  key={name}
                  onMouseEnter={!isGroup ? handleMinimizedHover : undefined}
                  style={{ marginBottom: '4px' }}
                >
                  <NavMenuItem
                    badgeLabel={badgeLabel}
                    childHasBadgeLabel={children?.some(
                      child => !!child.badgeLabel,
                    )}
                    hideLabel
                    icon={icon}
                    minimized
                    name={name}
                    onClick={isGroup ? handleMinimizedClick : undefined}
                    onHover={isGroup ? handleMinimizedClick : undefined}
                    onNavigate={
                      isGroup
                        ? () => clearMinimizedFlyout()
                        : () => clearMinimizedFlyout()
                    }
                    path={isGroup ? children?.[0]?.route?.path : route?.path}
                    search={
                      isGroup ? children?.[0]?.route?.search : route?.search
                    }
                    selected={isSelected || isMinimizedOpen}
                    suppressTooltip={isMinimizedOpen}
                    tooltip={tooltip}
                  />
                </li>
              </>
            );
          },
        )}
      </UnstyledUnorderedList>
    );
  }, [clearMinimizedFlyout, minimizedFlyout.openFor, navItems, pathname]);

  const navMenu = useMemo(() => {
    const hasLogs = navItems.some(item => item.name === Tabs.LOG);

    return (
      <UnstyledUnorderedList style={{ padding: '0px 8px' }}>
        {navItems.map(
          ({ badgeLabel, children, icon, name, route, tooltip }) => (
            <React.Fragment key={name}>
              {name === Tabs.LOG || (name === Tabs.SETTINGS && !hasLogs) ? (
                <li key={`${name}-sep`}>
                  <Separator />
                </li>
              ) : null}
              <NavGroup
                active={activeNavGroup === name}
                badgeLabel={badgeLabel}
                icon={icon}
                key={name}
                name={name}
                onToggle={() =>
                  setActiveNavGroup(prev => (prev === name ? null : name))
                }
                path={route?.path}
                search={route?.search}
                tooltip={tooltip}
              >
                {children}
              </NavGroup>
            </React.Fragment>
          ),
        )}
      </UnstyledUnorderedList>
    );
  }, [activeNavGroup, navItems]);

  const handleHeaderRender = () => {
    if (isFullWidth && isSidebarHovered) {
      return (
        <Tooltip
          placement='right'
          tooltipContent={t('sidebar.collapseTooltip')}
        >
          <ToggleContainer onClick={handleToggle}>
            <img
              src={ToggleCollapseIcon}
              style={{ height: '13px', width: '13px' }}
            />
          </ToggleContainer>
        </Tooltip>
      );
    }
  };

  return (
    <>
      <SidebarContainer
        onMouseEnter={() => setIsSidebarHovered(true)}
        onMouseLeave={() => setIsSidebarHovered(false)}
      >
        <SidebarSection>
          <SidebarHeader>
            <MinimizedSidebarItem
              isFullWidth={isFullWidth}
              style={{ marginLeft: '2px' }}
            >
              {/* Expand icon - visible only when minimized and hovered */}
              <span
                onClick={handleToggle}
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  inset: 0,
                  justifyContent: 'center',
                  opacity:
                    isSidebarHovered && !isFullWidth && !recentlyToggled
                      ? 1
                      : 0,
                  pointerEvents:
                    isSidebarHovered && !isFullWidth && !recentlyToggled
                      ? 'auto'
                      : 'none',
                  position: 'absolute',
                  transition: 'opacity 120ms linear',
                }}
              >
                <Tooltip
                  placement='right'
                  PopperProps={{
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 19],
                        },
                      },
                    ],
                  }}
                  tooltipContent={t('sidebar.expandTooltip')}
                >
                  <img
                    alt={t('sidebar.expandAlt')}
                    src={ToggleExpandIcon}
                    style={{ height: '13px', width: '13px' }}
                  />
                </Tooltip>
              </span>

              {/* Logo - visible when not showing expand icon */}
              <span
                onClick={handleToggle}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleToggle();
                  }
                }}
                role='button'
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  inset: 0,
                  justifyContent: 'center',
                  opacity:
                    isSidebarHovered && !isFullWidth && !recentlyToggled
                      ? 0
                      : 1,
                  pointerEvents:
                    isSidebarHovered && !isFullWidth && !recentlyToggled
                      ? 'none'
                      : 'auto',
                  position: 'absolute',
                  transition: 'opacity 120ms linear',
                }}
                tabIndex={0}
              >
                <img
                  alt={t('sidebar.logoAlt')}
                  height='28px'
                  src={logoMini}
                  style={{ cursor: 'pointer' }}
                />
              </span>
            </MinimizedSidebarItem>
            {handleHeaderRender()}
          </SidebarHeader>
          {isAIStudioEnabled && !isAIStudioNavbarUxEnabled && (
            <SidebarAITrigger
              isLoading={isAIStudioLoading}
              isMinimized={!isFullWidth}
              onOpenStudio={() =>
                openAIStudio({ entryMethod: 'sidebar_click' })
              }
            />
          )}
          {isFullWidth ? navMenu : minimizedNavMenu}
          {!isFullWidth && minimizedFlyout.openFor && (
            <MinimizedFlyout
              onMouseLeave={() =>
                setMinimizedFlyout({ left: null, openFor: null, top: null })
              }
              style={{
                left: minimizedFlyout.left ?? 48,
                top: minimizedFlyout.top ?? 0,
              }}
            >
              <UnstyledUnorderedList>
                <NavGroup
                  active={true}
                  badgeLabel={undefined}
                  icon={undefined}
                  key={minimizedFlyout.openFor ?? 'minimized-flyout'}
                  name={minimizedFlyout.openFor ?? ''}
                  onToggle={() =>
                    setMinimizedFlyout({
                      children: undefined,
                      left: null,
                      openFor: null,
                      top: null,
                    })
                  }
                  path={undefined}
                  search={undefined}
                >
                  {minimizedFlyout.children}
                </NavGroup>
              </UnstyledUnorderedList>
            </MinimizedFlyout>
          )}
        </SidebarSection>
      </SidebarContainer>
    </>
  );
};

const MinimizedSidebarItem = styled('div')<{
  isFullWidth?: boolean;
}>`
  width: 44px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: content-box;
  border-radius: 4px;
  cursor: pointer;
  ${props => (!props.isFullWidth ? 'margin: 0 auto;' : 'margin-left: 2px;')}

  ${props =>
    !props.isFullWidth
      ? `&:hover { background-color: ${props.theme.palette.colors.teal[700]}; }`
      : ''}

  img {
    position: static;
    height: 20px;
    width: auto;
    margin: 0;
    display: block;
  }
`;

const SidebarContainer = styled('nav')`
  background-color: ${({ theme }) => theme.palette.colors.primary[800]};
  padding: 0;
  ${props => (props.onClick ? 'cursor: pointer;' : undefined)}
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
`;

const MinimizedFlyout = styled('div')`
  position: fixed;
  width: auto;
  z-index: 999999;
  background: transparent;
  border-radius: 0;
  padding: 0;
`;

const ToggleContainer = styled('div')`
  width: 44px;
  height: 36px;
  border-radius: 4px;
  margin-right: 5px;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.palette.colors.primary[700]};
  }
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  img {
    position: static;
    height: 20px;
    width: auto;
    margin: 0;
    display: block;
  }
`;

const SidebarHeader = styled('div')`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 6px 30px 6px;
  border: none;
  background: transparent;
`;

const SidebarSection = styled('section')`
  flex: 1;
`;

const MemoizedSidebar = memo(NewSidebar);

export default MemoizedSidebar;

const Separator = styled('div')`
  height: 1px;
  background: ${SIDEBAR_HIGHLIGHT_COLOR};
  position: relative;
  left: -8px;
  width: calc(100% + 16px);
  margin: 4px 0;
`;
